import { useState, useEffect, useCallback } from "react";

const reviews = [
  {
    name: "Sandra Mitchell",
    location: "Northfleet",
    text: "Absolutely brilliant service! My windows haven't been this clean in years. The team was punctual, professional, and left no mess. I've already booked them for a regular clean every month.",
    stars: 5,
    platform: "Google",
    platformIcon: "ri-google-fill",
    date: "January 2026",
  },
  {
    name: "James Harrington",
    location: "Gravesend",
    text: "Used Mighty Cleaning for our office building and the results were outstanding. They worked efficiently around our business hours and the glass looks immaculate. Highly recommend to any commercial property owner.",
    stars: 5,
    platform: "Google",
    platformIcon: "ri-google-fill",
    date: "February 2026",
  },
  {
    name: "Claire Thompson",
    location: "Northfleet",
    text: "Very happy with the conservatory clean — it looks brand new! The price was fair and the guys were friendly and careful. Will definitely use again. Great local company.",
    stars: 5,
    platform: "Facebook",
    platformIcon: "ri-facebook-fill",
    date: "December 2025",
  },
  {
    name: "David Okafor",
    location: "Swanscombe",
    text: "Reliable, thorough, and excellent value. They've been doing my windows for six months now and every clean is consistently great. It's so nice to have a window cleaner you can actually depend on!",
    stars: 5,
    platform: "Google",
    platformIcon: "ri-google-fill",
    date: "March 2026",
  },
  {
    name: "Patricia Lawson",
    location: "Gravesend",
    text: "First class from start to finish. They responded quickly to my enquiry, gave a clear quote and arrived right on time. The windows look absolutely spotless. Couldn't ask for more.",
    stars: 5,
    platform: "Facebook",
    platformIcon: "ri-facebook-fill",
    date: "November 2025",
  },
  {
    name: "Mark Ellis",
    location: "Dartford",
    text: "I'd tried a couple of other window cleaners before but Mighty Cleaning are on another level. Professional kit, proper technique, and you can tell they genuinely care about doing a good job. Thoroughly recommend.",
    stars: 5,
    platform: "Google",
    platformIcon: "ri-google-fill",
    date: "January 2026",
  },
];

function ReviewCard({ review }: { review: typeof reviews[0] }) {
  return (
    <article className="bg-white rounded-2xl p-7 border border-gray-100 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <i className="ri-double-quotes-l text-[#1E88E5]/25 text-4xl leading-none"></i>
        <div className="flex items-center gap-1.5 bg-[#F8FAFE] rounded-lg px-3 py-1.5">
          <i className={`${review.platformIcon} text-sm ${review.platform === "Google" ? "text-[#EA4335]" : "text-[#1877F2]"}`}></i>
          <span className="text-gray-500 text-xs font-body">{review.platform}</span>
        </div>
      </div>
      <p className="text-gray-600 font-body text-sm leading-relaxed flex-1 mb-6">
        "{review.text}"
      </p>
      <div className="flex items-center justify-between pt-4 border-t border-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#EBF4FF] rounded-full flex-shrink-0 font-heading text-[#1A3A6B] text-sm font-bold">
            {review.name.charAt(0)}
          </div>
          <div>
            <span className="font-heading text-[#0F2040] text-sm font-bold block">{review.name}</span>
            <span className="text-gray-400 text-xs font-body">{review.location} · {review.date}</span>
          </div>
        </div>
        <div className="flex gap-0.5">
          {Array.from({ length: review.stars }).map((_, i) => (
            <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const total = reviews.length;

  const go = useCallback((nextIdx: number) => {
    setVisible(false);
    setTimeout(() => {
      setCurrent(nextIdx);
      setVisible(true);
    }, 280);
  }, []);

  const next = useCallback(() => go((current + 1) % total), [current, go, total]);
  const prev = useCallback(() => go((current - 1 + total) % total), [current, go, total]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const desktopCards = [
    reviews[current % total],
    reviews[(current + 1) % total],
    reviews[(current + 2) % total],
  ];

  return (
    <section className="py-24 bg-[#F8FAFE]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
            Customer Reviews
          </span>
          <h2
            className="font-heading text-[#0F2040] leading-tight mb-4"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.75rem)", fontWeight: 800 }}
          >
            What Our Customers Say
          </h2>
          <p className="text-gray-500 font-body text-base max-w-lg mx-auto mb-8">
            Don't just take our word for it — here's what our happy customers across Northfleet, Gravesend and Kent have to say.
          </p>
        </div>

        {/* Rating Badges */}
        <div className="flex flex-wrap justify-center gap-5 mb-12">
          <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-6 py-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#FEF0EE] rounded-lg flex-shrink-0">
              <i className="ri-google-fill text-[#EA4335] text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                {[1,2,3,4,5].map((i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                ))}
                <span className="font-heading text-[#0F2040] text-sm font-bold ml-1">4.9</span>
              </div>
              <span className="text-gray-400 text-xs font-body">Based on 50+ Google reviews</span>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-6 py-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#EEF4FF] rounded-lg flex-shrink-0">
              <i className="ri-facebook-fill text-[#1877F2] text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                {[1,2,3,4,5].map((i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                ))}
                <span className="font-heading text-[#0F2040] text-sm font-bold ml-1">4.8</span>
              </div>
              <span className="text-gray-400 text-xs font-body">Based on 30+ Facebook reviews</span>
            </div>
          </div>
        </div>

        {/* Carousel */}
        <div
          className={`transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
        >
          {/* Desktop: 3 cards */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            {desktopCards.map((review, i) => (
              <ReviewCard key={`desk-${current}-${i}`} review={review} />
            ))}
          </div>

          {/* Mobile: 1 card */}
          <div className="md:hidden">
            <ReviewCard review={reviews[current]} />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-6 mt-10">
          <button
            onClick={prev}
            aria-label="Previous review"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-[#1A3A6B] hover:bg-[#EBF4FF] hover:border-[#1E88E5] transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>

          {/* Dots */}
          <div className="flex items-center gap-2">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Go to review ${i + 1}`}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === current
                    ? "w-6 h-2.5 bg-[#1E88E5]"
                    : "w-2.5 h-2.5 bg-gray-200 hover:bg-gray-300"
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            aria-label="Next review"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-[#1A3A6B] hover:bg-[#EBF4FF] hover:border-[#1E88E5] transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>

        {/* Auto-advance indicator */}
        <p className="text-center text-gray-400 text-xs font-body mt-4">
          Auto-advancing · {current + 1} of {total}
        </p>
      </div>
    </section>
  );
}
